<template>
  <div>
    <skeleton v-if="$loading.isLoading('initUserDialog')"></skeleton>
    <div v-else>
      <v-row>
        <v-col cols="12" md="4" sm="12">
          <UserPhoto
            v-model="user.photo"
            :error-messages="getErrorMessages(v$.photo)"
            :user="user" />
        </v-col>
        <v-col cols="12" md="8" sm="12">
          <b-input
            v-model="user.surname"
            :error-messages="getErrorMessages(v$.surname)"
            label="Прізвище"
            placeholder="Введіть текст"
            @blur="v$.surname.$touch()"></b-input>
          <b-input
            v-model="user.name"
            :error-messages="getErrorMessages(v$.name)"
            label="Ім'я"
            placeholder="Введіть текст"
            @blur="v$.name.$touch()"></b-input>
          <b-input
            v-model="user.patronymic"
            :error-messages="getErrorMessages(v$.patronymic)"
            label="По батькові"
            placeholder="Введіть текст"
            @blur="v$.patronymic.$touch()"></b-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4" sm="12">
          <b-input
            v-model="user.inn"
            v-mask:[innMask]
            :error-messages="getErrorMessages(v$.inn)"
            hide-details
            label="ІПН"
            placeholder="Введіть номер"
            type="mask"
            @blur="v$.inn.$touch()"></b-input>
        </v-col>
        <v-col cols="12" md="4" sm="12">
          <b-select
            v-model="user.genderId"
            :error-messages="getErrorMessages(v$.genderId)"
            :items="$directory.get('genders')"
            hide-details
            item-title="name"
            item-value="id"
            label="Стать"
            placeholder="Оберіть зі списку"
            @blur="v$.genderId.$touch()"></b-select>
        </v-col>
        <v-col cols="12" md="4" sm="12">
          <b-input-date
            v-model="user.birthDate"
            :error-messages="getErrorMessages(v$.birthDate)"
            hide-details
            label="Дата народження"
            @blur="v$.birthDate.$touch()"></b-input-date>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4" sm="12">
          <b-input-phone
            v-model="user.phone"
            :error-messages="getErrorMessages(v$.phone)"
            hide-details
            label="Телефон"
            @blur="v$.phone.$touch()"></b-input-phone>
        </v-col>
        <v-col cols="12" md="4" sm="12">
          <b-input
            v-model="user.email"
            v-mask:[emailMask]
            :error-messages="getErrorMessages(v$.email)"
            hide-details
            label="Пошта"
            placeholder="Введіть текст"
            @blur="v$.email.$touch()"></b-input>
        </v-col>
        <v-col cols="12" md="4" sm="12">
          <b-input
            v-model="user.profileDWH"
            hide-details
            label="profileDWH"
            placeholder="Введіть текст"></b-input>
        </v-col>
      </v-row>
      <v-row class="mb-3">
        <v-col cols="12" md="6">
          <b-select
            v-model="user.workScheduleId"
            :items="$directory.get('workSchedules')"
            hide-details
            label="Графік роботи"
            @focus="$directory.fill('workSchedules')" />
        </v-col>
        <v-col cols="12" md="6">
          <b-select
            v-model="user.workFormatId"
            :items="$directory.get('workFormats')"
            hide-details
            label="Формат роботи"
            @focus="$directory.fill('workFormats')" />
        </v-col>
        <v-col cols="12" md="6">
          <b-input
            v-model="user.password"
            :error-messages="getErrorMessages(v$.password)"
            class="w-100 align-self-start"
            hide-details
            label="Пароль"
            placeholder="********"
            @blur="v$.password.$touch()">
            <template #append-inner>
              <div class="d-flex align-center">
                <v-tooltip text="Згенерувати пароль">
                  <template #activator="{ props }">
                    <v-btn
                      color="grey"
                      density="compact"
                      icon="mdi-refresh"
                      size="small"
                      v-bind="props"
                      variant="text"
                      @click.stop="generateSuggestedPassword"></v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip text="Скопіювати пароль">
                  <template #activator="{ props }">
                    <v-btn
                      color="grey"
                      density="compact"
                      icon="mdi-content-copy"
                      size="small"
                      v-bind="props"
                      variant="text"
                      @click.stop="copySuggestedPassword"></v-btn>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </b-input>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { emailMask, innMask } from '@/utils/masks.js'
import { reactive, ref, watch } from 'vue'
import { email, required, useVuelidate } from '@/plugins'
import { getErrorMessages } from '@/utils/helpers'
import { BInputDate, BInputPhone } from 'best-modules/components'
import UserPhoto from '@/dialog/components/user-card/UserPhoto.vue'
import { fillDirectory } from '@/directory/index.ts'
import { userCreate } from '@/requests/user'
import { copyText } from 'best-modules/vue-utils'
import Skeleton from '@/components/Skeleton.vue'
import { handleAsync } from 'best-modules/plugins'

export default {
  name: 'User',
  components: {
    UserPhoto,
    BInputPhone,
    BInputDate,
    Skeleton,
  },
  methods: { getErrorMessages },
  async setup() {
    handleAsync('initUserDialog', () => {
      return fillDirectory([
        'departmentList',
        'roles',
        'genders',
        'workSchedules',
        'workFormats',
      ])
    })

    const user = reactive({
      name: null,
      surname: null,
      patronymic: null,
      genderId: null,
      birthDate: null,
      inn: null,
      phone: null,
      email: null,
      profileDWH: null,
      password: null,
      photo: null,
      workScheduleId: null,
      workFormatId: null,
    })

    const lastGeneratedPassword = ref('')

    const transliterationMap = {
      а: 'a',
      б: 'b',
      в: 'v',
      г: 'h',
      ґ: 'g',
      д: 'd',
      е: 'e',
      є: 'ye',
      ё: 'yo',
      ж: 'zh',
      з: 'z',
      и: 'y',
      і: 'i',
      ї: 'yi',
      й: 'y',
      к: 'k',
      л: 'l',
      м: 'm',
      н: 'n',
      о: 'o',
      п: 'p',
      р: 'r',
      с: 's',
      т: 't',
      у: 'u',
      ф: 'f',
      х: 'kh',
      ц: 'ts',
      ч: 'ch',
      ш: 'sh',
      щ: 'shch',
      ъ: '',
      ы: 'y',
      ь: '',
      э: 'e',
      ю: 'yu',
      я: 'ya',
    }

    const getPasswordNamePart = () => {
      const source = user.surname || 'User'
      const transliterated = source
        .trim()
        .toLowerCase()
        .split('')
        .map(letter => transliterationMap[letter] || letter)
        .join('')
        .replace(/[^a-z0-9]/g, '')

      const namePart = transliterated || 'User'
      return namePart.charAt(0).toUpperCase() + namePart.slice(1)
    }

    const getBirthDateDigits = () => {
      if (!user.birthDate || typeof user.birthDate !== 'string') return null

      const isoDate = user.birthDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoDate) return `${isoDate[2]}${isoDate[3]}`

      const localDate = user.birthDate.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
      if (localDate) return `${localDate[2]}${localDate[1]}`

      return null
    }

    const getRandomDigits = () => {
      return String(Math.floor(1000 + Math.random() * 9000))
    }

    const generateSuggestedPassword = () => {
      lastGeneratedPassword.value = `${getPasswordNamePart()}@${
        getBirthDateDigits() || getRandomDigits()
      }`
      user.password = lastGeneratedPassword.value
    }

    const copySuggestedPassword = () => {
      copyText(user.password || '')
    }

    generateSuggestedPassword()

    watch([() => user.surname, () => user.birthDate], () => {
      if (!user.password || user.password === lastGeneratedPassword.value) {
        generateSuggestedPassword()
      }
    })

    const v$ = useVuelidate(
      {
        name: { required },
        surname: { required },
        patronymic: { required },
        genderId: { required },
        birthDate: { required },
        inn: { required },
        photo: { required },
        phone: { required },
        email: { required, email },
        password: { required },
      },
      user
    )

    const submit = () => {
      return userCreate(user).then(res => {
        return res
      })
    }

    return {
      user,
      v$,
      submit,
      generateSuggestedPassword,
      copySuggestedPassword,
      innMask,
      emailMask,
      validationScope: 'user',
    }
  },
}
</script>
